import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { ariesTemplateFileAtom, ariesTemplateUploadBaseAtom } from './atoms';

/**
 * The combined state of the two-step "Load a new Template" upload:
 *  - `idle`        — nothing picked yet.
 *  - `uploading`   — the Active Storage direct upload (browser → blob storage).
 *  - `registering` — the direct upload finished; POST /aries_templates in flight.
 *  - `complete`    — the template row exists; `templateId` is set.
 *  - `error`       — oversize / upload failure / a 422 from POST /aries_templates.
 */
export type AriesTemplateUploadStatus =
  'idle' | 'uploading' | 'registering' | 'complete' | 'error';

interface UseAriesTemplateUploadReturn {
  /** The rolled-up status across the direct upload + the register POST. */
  status: AriesTemplateUploadStatus;
  /** The chosen file's name (from the picked file, then the created template). */
  fileName: string | undefined;
  /** The created template's id once registered — feeds the kickoff `templateId`. */
  templateId: number | null;
  /** The created template's server filename (for the current-default refresh). */
  uploadedTemplateName: string | null;
  /** The inline error to surface in the drop zone, or null. */
  error: string | null;
  /** Whether the upload/register is in flight (drop zone + Export stay disabled). */
  isBusy: boolean;
  /** Kick off the direct upload for a freshly picked file. */
  upload: (file: File) => void;
  /** Clear the selection + any error (on remove, source switch, or modal open). */
  reset: () => void;
}

/**
 * Owns the in-modal "Load a new Template" upload sub-flow (INS-10555): the
 * Active Storage direct upload followed by `POST /aries_templates` to register
 * the blob as an `AriesTemplate`. Exposes a single rolled-up status so the drop
 * zone can render one lifecycle, and the created template's `id` so the modal
 * can pass it as the kickoff's `templateId`.
 *
 * The direct upload is fire-and-forget (the file atom flips to `complete` with a
 * signed id when done), so registration is triggered from an effect watching the
 * atom — guarded by a ref so the POST fires exactly once per uploaded blob.
 *
 * @param {boolean} makeDefault - Whether to set the uploaded template as the global default.
 * @return {UseAriesTemplateUploadReturn} Upload status, the resulting template id, and actions.
 */
export const useAriesTemplateUpload = (
  makeDefault: boolean
): UseAriesTemplateUploadReturn => {
  const [fileState, setFile] = useAtom(ariesTemplateFileAtom);
  const registerTemplate = useSetAtom(ariesTemplateUploadBaseAtom);

  const [templateId, setTemplateId] = useState<number | null>(null);
  const [uploadedTemplateName, setUploadedTemplateName] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  // The signed id we've already registered (or are registering). The register
  // effect re-runs on every render while the file atom stays `complete`, so this
  // guards against a duplicate POST for the same blob. Also nulled on reset so a
  // stale `complete` from the module-level atom can't auto-register on reopen.
  const registeredSignedId = useRef<string | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setTemplateId(null);
    setUploadedTemplateName(null);
    setIsRegistering(false);
    registeredSignedId.current = null;
    // `undefined` resets the file atom back to its `edit` (idle) state.
    setFile(undefined);
  }, [setFile]);

  const upload = useCallback(
    (file: File) => {
      setError(null);
      setTemplateId(null);
      setUploadedTemplateName(null);
      registeredSignedId.current = null;
      // Kicks off the Active Storage direct upload; the atom flips to `uploading`
      // then `complete` (with a signed id) or `error`.
      setFile(file);
    },
    [setFile]
  );

  useEffect(() => {
    // Surface a direct-upload failure (oversize / network) as the inline error.
    if (fileState.status === 'error') {
      setError(fileState.error ?? 'Failed to upload template');
      return;
    }
    const signedId = fileState.file;
    if (
      fileState.status !== 'complete' ||
      !signedId ||
      registeredSignedId.current === signedId
    ) {
      return;
    }
    // Direct upload done → register the blob as a template exactly once.
    registeredSignedId.current = signedId;
    setIsRegistering(true);
    setError(null);
    registerTemplate(signedId, makeDefault)
      .then((template) => {
        setTemplateId(template.id);
        setUploadedTemplateName(template.fileName);
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : 'Failed to upload template'
        );
        setTemplateId(null);
      })
      .finally(() => setIsRegistering(false));
  }, [
    fileState.status,
    fileState.file,
    fileState.error,
    registerTemplate,
    makeDefault,
  ]);

  const status: AriesTemplateUploadStatus = error
    ? 'error'
    : templateId !== null
      ? 'complete'
      : isRegistering
        ? 'registering'
        : fileState.status === 'uploading'
          ? 'uploading'
          : 'idle';

  return {
    status,
    fileName: uploadedTemplateName ?? fileState.name,
    templateId,
    uploadedTemplateName,
    error,
    isBusy: status === 'uploading' || status === 'registering',
    upload,
    reset,
  };
};
